import { useMemo } from 'react';
import { IS_DEVELOPMENT } from 'src/config';
import type { FeatureFlagState } from './featureFlagAtom';
import {
  useFeatureFlagAtomSetter,
  useFeatureFlagAtomValues,
} from './featureFlagAtom';
import type { FeatureFlag } from './featureFlags';
import { defaultFeatureFlags } from './featureFlags';
import { useLaunchDarklyFlags } from './useLaunchDarklyFlags';

export const useFeatureFlags = () => {
  const ldFlags = useLaunchDarklyFlags();
  const storedFlags = useFeatureFlagAtomValues();

  const state = useMemo(() => {
    const finalFlags: Record<FeatureFlag, boolean> = { ...defaultFeatureFlags };

    (Object.keys(defaultFeatureFlags) as FeatureFlag[]).forEach((key) => {
      if (IS_DEVELOPMENT && storedFlags.overrides[key]) {
        finalFlags[key] = storedFlags.flags[key];
      } else if (key in ldFlags) {
        finalFlags[key] = !!ldFlags[key];
      } else {
        finalFlags[key] = storedFlags.flags[key];
      }
    });

    return finalFlags;
  }, [storedFlags, ldFlags]);

  return state;
};

export const useFeatureFlagOverrides = () => {
  const storedFlags = useFeatureFlagAtomValues();
  return storedFlags.overrides;
};

export const useSetFeatureFlag = () => {
  const setFeatureFlags = useFeatureFlagAtomSetter();

  return (key: FeatureFlag, value: boolean, override = false) => {
    setFeatureFlags(
      (prev: FeatureFlagState) =>
        ({
          flags: {
            ...prev.flags,
            [key]: value,
          },
          overrides: {
            ...prev.overrides,
            [key]: override,
          },
        }) satisfies FeatureFlagState
    );
  };
};

export const useResetFeatureFlags = () => {
  const setFeatureFlags = useFeatureFlagAtomSetter();

  return () => {
    setFeatureFlags({
      flags: defaultFeatureFlags,
      overrides: Object.fromEntries(
        Object.keys(defaultFeatureFlags).map((key) => [key, false])
      ) as Record<FeatureFlag, boolean>,
    } satisfies FeatureFlagState);
  };
};

export const useChangedFeatureFlagsList = () => {
  const featureFlags = useFeatureFlags();
  const launchDarklyFlags = useLaunchDarklyFlags();

  const result = Object.entries(featureFlags)
    .filter(([key, value]) => {
      if (key in launchDarklyFlags) {
        return (
          launchDarklyFlags[key as keyof typeof launchDarklyFlags] !== value
        );
      }
      return (
        defaultFeatureFlags[key as keyof typeof defaultFeatureFlags] !== value
      );
    })
    .map(([key]) => key);
  return result;
};
